import React from 'react';

import { Card } from '@/components/ui/cards';
import { Text } from '@/components/ui/text/Text';
import { HourCounterBadge } from '@/components/ui/badges';
import { Banner } from '@/components/ui/banners';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { PermissionGuard } from '@/components/layout';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import { ShiftDayCard } from './ShiftDayCard';
import type { UseShiftWeeklyDistributionResult } from '@/hooks/shift/useShiftWeeklyDistribution';


const NOOP_DAY_CLICK = (): void => {};

interface ShiftWeeklyDistributionGridProps extends UseShiftWeeklyDistributionResult {
  onCancel: () => void;
  /**
   * Permission action guarding the grid's own Save button — 'create' for the
   * Create flow (default, unchanged), 'edit' for the Edit flow (ASIS-5045).
   */
  permissionAction?: 'create' | 'edit';
}

export const ShiftWeeklyDistributionGrid: React.FC<ShiftWeeklyDistributionGridProps> = ({
  weeks,
  showWeekHeaders,
  cycleCounter,
  isSaveDisabled,
  isSaving,
  isReadOnly = false,
  openAssignPanel,
  onSave,
  onCancel,
  permissionAction = 'create',
}) => {
  const { t: translate } = useTranslation('schedule');

  return (
    <div className="mt-6" data-testid="shift-weekly-distribution-grid">
      {/* `padding=""`: sin esto, el padding inline por defecto de Card (8px) gana
          sobre las clases de Tailwind y el contenido queda pegado al borde. */}
      <Card padding="" className="p-6 md:p-8">
        {/* Mismo estilo de encabezado que FormLayout.Section ("Descripción"). */}
        <div className="flex items-center justify-between mb-6">
          <Text variant="body-medium" as="h2" color={colors.primary.dash}>
            {translate('shift.create.sections.weeklyDistribution')}
          </Text>
          {cycleCounter && (
            <HourCounterBadge value={cycleCounter.value} variant={cycleCounter.variant} />
          )}
        </div>

        {cycleCounter?.warningMessage && (
          <Banner
            variant={cycleCounter.variant}
            density="compact"
            text={cycleCounter.warningMessage}
            data-testid="cycle-counter-warning-banner"
          />
        )}

        {weeks.map((week) => (
          <div key={week.weekNumber} className="mb-8">
            {showWeekHeaders && (
              <div className="flex items-center justify-between mb-2">
                <Text variant="subheader-medium" color={colors.primary.dash}>
                  {translate('shift.create.assignSchedule.weekHeader', { week: week.weekNumber })}
                </Text>
                {week.counter && (
                  <HourCounterBadge value={week.counter.value} variant={week.counter.variant} />
                )}
              </div>
            )}

            {week.counter?.warningMessage && (
              <Banner
                variant={week.counter.variant}
                density="compact"
                text={week.counter.warningMessage}
                data-testid="biweekly-block-warning-banner"
              />
            )}

            <div className="grid grid-cols-7 gap-4">
              {week.days.map((day) => (
                <ShiftDayCard
                  key={day.daySequence}
                  day={day}
                  onClick={isReadOnly ? NOOP_DAY_CLICK : openAssignPanel}
                />
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-center gap-4 mt-8">
          <PrimaryButton variant="outline" onClick={onCancel} data-testid="shift-grid-cancel-button">
            {translate('common:actions.cancel')}
          </PrimaryButton>
          {!isReadOnly && (
            <PermissionGuard action={permissionAction}>
              <PrimaryButton
                onClick={onSave}
                disabled={isSaveDisabled}
                isLoading={isSaving}
                data-testid="shift-grid-save-button"
              >
                {translate('common:actions.save')}
              </PrimaryButton>
            </PermissionGuard>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ShiftWeeklyDistributionGrid;
